/**
 * Own-ship vector toggles — which of the four vectors reach off our own boat:
 *   - cogsog   the course/speed ground vector (two arrowheads), from the fix
 *   - heading  the heading / water-track vector (one arrowhead), needs a heading source
 *   - wind     a downwind arrow, needs a wind source
 *   - tide     the tidal set/rate arrow, needs tidal-stream data for the area
 *
 * COG/SOG and heading come straight off the position fix, so they default on and
 * match how the boat has always drawn. Wind and tide need a data source that may
 * not be connected, so they default off; the Own-ship vectors menu turns them on
 * and points us at the right source when one is missing. Kept on the device
 * (localStorage) like the range-rings overlay so the setup survives a reload.
 */
import { create } from 'zustand';

const COG_LS = 'acp.ownVectors.cogsog.v1';
const HDG_LS = 'acp.ownVectors.heading.v1';
const WIND_LS = 'acp.ownVectors.wind.v1';
const TIDE_LS = 'acp.ownVectors.tide.v1';

function bool(key: string, def: boolean): boolean {
  try {
    const v = typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
    return v == null ? def : v === '1';
  } catch {
    return def;
  }
}
function save(key: string, v: boolean): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(key, v ? '1' : '0');
  } catch {
    /* ignore quota / private-mode / SSR */
  }
}

interface OwnVectorsState {
  /** Course/speed ground vector (from the fix). */
  cogsog: boolean;
  setCogsog: (b: boolean) => void;
  /** Heading / water-track vector (needs a heading source). */
  heading: boolean;
  setHeading: (b: boolean) => void;
  /** Downwind arrow (needs a wind source). */
  wind: boolean;
  setWind: (b: boolean) => void;
  /** Tidal set/rate arrow (needs tidal-stream data for the area). */
  tide: boolean;
  setTide: (b: boolean) => void;
}

export const useOwnVectorsStore = create<OwnVectorsState>((set) => ({
  cogsog: bool(COG_LS, true),
  setCogsog: (cogsog) => {
    save(COG_LS, cogsog);
    set({ cogsog });
  },
  heading: bool(HDG_LS, true),
  setHeading: (heading) => {
    save(HDG_LS, heading);
    set({ heading });
  },
  wind: bool(WIND_LS, false),
  setWind: (wind) => {
    save(WIND_LS, wind);
    set({ wind });
  },
  tide: bool(TIDE_LS, false),
  setTide: (tide) => {
    save(TIDE_LS, tide);
    set({ tide });
  },
}));
